import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Col,
  Container,
  Form,
  Nav,
  Row,
  Spinner,
  Tab,
  Table,
} from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiGet, apiPatch, apiPost, getApiBase } from '../../services/api';
import CompanyMessages from './CompanyMessages';
import CompanySettingsTab from './dashboard/CompanySettingsTab';
import CompanyTeamTab from './dashboard/CompanyTeamTab';
import EditJobModal from './dashboard/EditJobModal';

const APPLICATION_STATUSES = [
  { value: 'received', label: 'Received' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'shortlisted', label: 'Shortlisted' },
  { value: 'interview_scheduled', label: 'Interview Scheduled' },
  { value: 'not_selected', label: 'Not Selected' },
  { value: 'hired', label: 'Hired' },
];

const JOB_DISPLAY_LABELS = {
  active: 'Active',
  pending: 'Pending Review',
  expired: 'Expired',
};

function formatPostedDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function jobStatusBadgeVariant(displayStatus) {
  if (displayStatus === 'active') return 'success';
  if (displayStatus === 'pending') return 'warning';
  return 'secondary';
}

function isEditWindowActive(job) {
  if (job.status !== 'approved' || !job.edit_window_ends_at) return false;
  return new Date(job.edit_window_ends_at) > new Date();
}

function editWindowDaysLeft(iso) {
  if (!iso) return 0;
  const end = new Date(iso).getTime();
  const now = Date.now();
  if (end <= now) return 0;
  return Math.max(1, Math.ceil((end - now) / (24 * 60 * 60 * 1000)));
}

function JobsTable({
  jobs,
  onViewApplications,
  showArchive,
  onArchive,
  onEdit,
  onFeature,
  featuringJobId,
  testIdPrefix,
  canManageJobs = true,
}) {
  if (!jobs.length) {
    return (
      <p className="text-muted mb-0" data-testid={`${testIdPrefix}-empty`}>
        No jobs yet.
      </p>
    );
  }

  return (
    <div className="table-responsive">
      <Table className="rwm-company-dashboard__table align-middle mb-0">
        <thead>
          <tr>
            <th>Job Title</th>
            <th>Status</th>
            <th>Applications</th>
            <th>Posted</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => (
            <tr key={job.id} data-testid={`${testIdPrefix}-row-${job.id}`}>
              <td>{job.title}</td>
              <td>
                <Badge
                  bg={jobStatusBadgeVariant(job.display_status)}
                  className="rwm-company-dashboard__status-badge"
                  data-testid={`rwm-job-status-${job.id}`}
                >
                  {job.display_label || JOB_DISPLAY_LABELS[job.display_status] || job.display_status}
                </Badge>
                {isEditWindowActive(job) ? (
                  <div
                    className="small text-muted mt-1"
                    data-testid={`${testIdPrefix}-edit-window-${job.id}`}
                  >
                    Edit window closes in {editWindowDaysLeft(job.edit_window_ends_at)} days
                  </div>
                ) : null}
                {job.display_status === 'active' && job.is_featured ? (
                  <Badge
                    bg="warning"
                    text="dark"
                    className="mt-1"
                    data-testid={`rwm-company-job-featured-badge-${job.id}`}
                  >
                    ★ Featured
                  </Badge>
                ) : null}
              </td>
              <td data-testid={`${testIdPrefix}-applications-${job.id}`}>{job.application_count}</td>
              <td>{formatPostedDate(job.posted_at)}</td>
              <td className="rwm-company-dashboard__actions">
                {canManageJobs && isEditWindowActive(job) && onEdit ? (
                  <Button
                    variant="outline-info"
                    size="sm"
                    className="me-2"
                    data-testid={`${testIdPrefix}-edit-${job.id}`}
                    onClick={() => onEdit(job)}
                  >
                    Edit
                  </Button>
                ) : null}
                {onViewApplications ? (
                  <Button
                    variant="outline-info"
                    size="sm"
                    className="me-2"
                    data-testid={`${testIdPrefix}-view-applications-${job.id}`}
                    onClick={() => onViewApplications(job.id)}
                  >
                    View Applications
                  </Button>
                ) : null}
                {canManageJobs && showArchive && job.display_status === 'active' && !job.is_featured && onFeature ? (
                  <Button
                    variant="outline-warning"
                    size="sm"
                    className="me-2"
                    disabled={featuringJobId === job.id}
                    data-testid={`rwm-company-job-feature-btn-${job.id}`}
                    onClick={() => onFeature(job.id)}
                  >
                    {featuringJobId === job.id ? 'Opening…' : '⭐ Feature this listing'}
                  </Button>
                ) : null}
                {canManageJobs && showArchive ? (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={!job.can_archive}
                    title={
                      job.can_archive
                        ? 'Archive this job'
                        : 'Resolve all open applications before archiving'
                    }
                    data-testid={`${testIdPrefix}-archive-${job.id}`}
                    onClick={() => onArchive?.(job.id)}
                  >
                    Archive
                  </Button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

export default function CompanyDashboard() {
  const { user, token, authLoading, isVerified } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'overview');
  const [submittedBanner, setSubmittedBanner] = useState(searchParams.get('submitted') === '1');
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [jobFilter, setJobFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [appsLoading, setAppsLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [error, setError] = useState('');
  const [statusErrors, setStatusErrors] = useState({});
  const [updatingId, setUpdatingId] = useState(null);
  const [cvDownloadingId, setCvDownloadingId] = useState(null);
  const [editJobId, setEditJobId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [featuringJobId, setFeaturingJobId] = useState(null);
  const [blockUpdatingId, setBlockUpdatingId] = useState(null);

  const handleAuthError = useCallback(
    (res) => {
      if (res.status === 401) {
        navigate('/login', { replace: true });
        return true;
      }
      if (res.status === 403) {
        setAccessDenied(true);
        return true;
      }
      return false;
    },
    [navigate]
  );

  const loadDashboard = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    setAccessDenied(false);
    try {
      const [statsRes, jobsRes, unreadRes] = await Promise.all([
        apiGet('/api/v1/company/dashboard/stats', token),
        apiGet('/api/v1/company/jobs', token),
        apiGet('/api/v1/company/messages/unread-count', token),
      ]);
      if (handleAuthError(statsRes) || handleAuthError(jobsRes) || handleAuthError(unreadRes)) return;
      if (!statsRes.ok || !jobsRes.ok) {
        setError('Could not load dashboard data. Please try again.');
        return;
      }
      const statsData = await statsRes.json();
      const jobsData = await jobsRes.json();
      setStats(statsData.data ?? null);
      setJobs(jobsData.data ?? []);
      if (unreadRes.ok) {
        const unreadData = await unreadRes.json();
        setUnreadMessageCount(unreadData.data?.count ?? 0);
      }
    } catch {
      setError('Could not load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token, handleAuthError]);

  const handleArchive = useCallback(
    async (jobId) => {
      if (!window.confirm('Archive this job? This cannot be undone.')) return;
      setUpdatingId(jobId);
      try {
        const res = await apiPost(`/api/v1/company/jobs/${jobId}/archive`, {}, token);
        if (handleAuthError(res)) return;
        if (!res.ok) {
          setError('Could not archive job.');
          return;
        }
        await loadDashboard();
      } finally {
        setUpdatingId(null);
      }
    },
    [token, handleAuthError, loadDashboard]
  );

  const handleFeatureJob = useCallback(
    async (jobId) => {
      setFeaturingJobId(jobId);
      setError('');
      try {
        const res = await apiPost(`/api/v1/company/jobs/${jobId}/feature`, {}, token);
        if (handleAuthError(res)) return;
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setError(data?.message || 'Could not start featured checkout.');
          return;
        }
        const data = await res.json();
        const checkoutUrl = data?.data?.checkout_url;
        if (checkoutUrl) {
          window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
        }
      } catch {
        setError('Could not start featured checkout.');
      } finally {
        setFeaturingJobId(null);
      }
    },
    [token, handleAuthError]
  );

  const loadApplications = useCallback(async () => {
    if (!token) {
      setApplications([]);
      return;
    }
    setAppsLoading(true);
    setError('');
    try {
      const res = await apiGet('/api/v1/company/applications', token);
      if (handleAuthError(res)) return;
      if (!res.ok) {
        setError('Could not load applications. Please try again.');
        return;
      }
      const data = await res.json();
      const all = (data.data ?? []).sort((a, b) => {
        const da = new Date(a.applied_at || 0).getTime();
        const db = new Date(b.applied_at || 0).getTime();
        return db - da;
      });
      setApplications(all);
    } catch {
      setError('Could not load applications. Please try again.');
    } finally {
      setAppsLoading(false);
    }
  }, [token, handleAuthError]);

  const handleBlockToggle = useCallback(
    async (app) => {
      if (!token || !app.candidate_id) return;
      setBlockUpdatingId(app.id);
      try {
        const path = `/api/v1/company/candidates/${app.candidate_id}/block`;
        const res = app.is_blocked
          ? await fetch(`${getApiBase()}${path}`, {
              method: 'DELETE',
              headers: {
                Accept: 'application/json',
                Authorization: `Bearer ${token}`,
              },
            })
          : await apiPost(path, {}, token);
        if (handleAuthError(res)) return;
        if (!res.ok) return;
        setApplications((prev) =>
          prev.map((row) =>
            row.id === app.id ? { ...row, is_blocked: !row.is_blocked } : row
          )
        );
      } finally {
        setBlockUpdatingId(null);
      }
    },
    [token, handleAuthError]
  );

  useEffect(() => {
    if (authLoading) return;
    if (!token || !user) {
      navigate('/login', { replace: true });
      return;
    }
    if (user.role !== 'company') {
      setAccessDenied(true);
      setLoading(false);
      return;
    }
    loadDashboard();
  }, [authLoading, token, user, navigate, loadDashboard]);

  useEffect(() => {
    if (activeTab === 'applications') {
      loadApplications();
    }
  }, [activeTab, loadApplications]);

  function openApplicationsForJob(jobId) {
    setJobFilter(String(jobId));
    setActiveTab('applications');
  }

  function openEditModal(job) {
    setEditJobId(job.id);
    setShowEditModal(true);
  }

  function closeEditModal() {
    setShowEditModal(false);
    setEditJobId(null);
  }

  function handleEditSaved() {
    closeEditModal();
    loadDashboard();
  }

  async function handleCvDownload(applicationId) {
    if (!token) return;
    setCvDownloadingId(applicationId);
    setError('');
    try {
      const res = await apiGet(`/api/v1/company/applications/${applicationId}/cv`, token);
      if (handleAuthError(res)) return;
      if (!res.ok) {
        setError('Could not download CV. Please try again.');
        return;
      }
      const data = await res.json();
      const url = data?.data?.url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        setError('Could not download CV. Please try again.');
      }
    } catch {
      setError('Could not download CV. Please try again.');
    } finally {
      setCvDownloadingId(null);
    }
  }

  async function handleStatusChange(applicationId, newStatus) {
    if (!token) return;
    setUpdatingId(applicationId);
    setStatusErrors((prev) => {
      const next = { ...prev };
      delete next[applicationId];
      return next;
    });
    try {
      const res = await apiPatch(
        `/api/v1/company/applications/${applicationId}/status`,
        { status: newStatus },
        token
      );
      if (handleAuthError(res)) return;
      const data = await res.json().catch(() => ({}));
      if (res.status === 422) {
        const msg =
          data?.errors?.status?.[0] || data?.message || 'Invalid status transition.';
        setStatusErrors((prev) => ({ ...prev, [applicationId]: msg }));
        return;
      }
      if (!res.ok) {
        setStatusErrors((prev) => ({
          ...prev,
          [applicationId]: 'Could not update status. Please try again.',
        }));
        return;
      }
      const updated = data.data;
      setApplications((prev) =>
        prev.map((app) => (app.id === applicationId ? { ...app, ...updated } : app))
      );
      await loadDashboard();
    } catch {
      setStatusErrors((prev) => ({
        ...prev,
        [applicationId]: 'Could not update status. Please try again.',
      }));
    } finally {
      setUpdatingId(null);
    }
  }

  if (authLoading || (loading && !accessDenied)) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status" data-testid="rwm-dashboard-loading">
          <span className="visually-hidden">Loading…</span>
        </Spinner>
      </Container>
    );
  }

  if (accessDenied) {
    return (
      <Container className="py-5">
        <Alert variant="danger" data-testid="rwm-dashboard-access-denied">
          Access denied. This page is for company accounts only.
        </Alert>
      </Container>
    );
  }

  const recentJobs = jobs.slice(0, 5);
  const canManageJobs = stats?.can_manage_jobs === true;
  const canManageApplications = stats?.can_manage_applications === true;
  const canBilling = stats?.can_billing === true;
  const canManageTeam = stats?.can_manage_team === true;
  const canManageProfile = stats?.can_manage_profile === true;
  const filteredApplications =
    jobFilter === 'all'
      ? applications
      : applications.filter((app) => String(app.job_id) === String(jobFilter));


  return (
    <Container className="py-4 rwm-company-dashboard">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
        <h1 className="h3 mb-0 rwm-company-dashboard__title">Company Dashboard</h1>
        {stats?.is_approved === false || !canManageJobs ? null : (
          <Button
            as={Link}
            to="/company/post-job"
            variant="info"
            data-testid="rwm-dashboard-post-job"
          >
            Post a Job
          </Button>
        )}
      </div>

      {stats?.is_approved === false ? (
        <Alert variant="warning" className="mb-4" data-testid="rwm-dashboard-unapproved-gate">
          Your company profile is pending admin approval. You can browse the dashboard, but posting
          jobs is unavailable until an admin approves your company.
        </Alert>
      ) : null}

      {submittedBanner ? (
        <Alert
          variant="success"
          className="mb-4"
          dismissible
          onClose={() => {
            setSubmittedBanner(false);
            const next = new URLSearchParams(searchParams);
            next.delete('submitted');
            setSearchParams(next, { replace: true });
          }}
          data-testid="rwm-dashboard-submitted-banner"
        >
          Your job was submitted and is pending manual review.
        </Alert>
      ) : null}

      {!isVerified ? (
        <Alert variant="warning" className="mb-4">
          Your email is not verified. Check your inbox or{' '}
          <Link to="/verify-email-pending" className="alert-link">
            resend the verification email
          </Link>
          .
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="danger" className="mb-4" data-testid="rwm-dashboard-error">
          {error}
        </Alert>
      ) : null}

      <Tab.Container activeKey={activeTab} onSelect={(k) => k && setActiveTab(k)}>
        <Nav variant="tabs" className="rwm-company-dashboard__nav mb-4">
          <Nav.Item>
            <Nav.Link eventKey="overview" data-testid="rwm-dashboard-tab-overview">
              Overview
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="jobs" data-testid="rwm-dashboard-tab-jobs">
              My Jobs
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="applications" data-testid="rwm-dashboard-tab-applications">
              Applications
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="messages" data-testid="rwm-dashboard-tab-messages">
              Messages{' '}
              {unreadMessageCount > 0 ? (
                <Badge bg="danger" data-testid="rwm-messages-unread-badge">
                  {unreadMessageCount}
                </Badge>
              ) : null}
            </Nav.Link>
          </Nav.Item>
          {canManageProfile ? (
            <Nav.Item>
              <Nav.Link eventKey="settings" data-testid="rwm-dashboard-tab-settings">
                Settings
              </Nav.Link>
            </Nav.Item>
          ) : null}
          {canManageTeam ? (
            <Nav.Item>
              <Nav.Link eventKey="team" data-testid="rwm-company-tab-team">
                Team
              </Nav.Link>
            </Nav.Item>
          ) : null}
          {canBilling ? (
            <Nav.Item>
              <Nav.Link
                as={Link}
                to="/company/billing"
                data-testid="rwm-dashboard-tab-billing"
              >
                Billing
              </Nav.Link>
            </Nav.Item>
          ) : null}
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="overview">
            <Row className="g-3 mb-4">
              <Col md={3} sm={6}>
                <div className="rwm-company-dashboard__stat-card">
                  <div className="rwm-company-dashboard__stat-label">Active Jobs</div>
                  <div
                    className="rwm-company-dashboard__stat-value rwm-company-dashboard__stat-value--blue"
                    data-testid="rwm-dashboard-stat-active-jobs"
                  >
                    {stats?.active_jobs ?? 0}
                  </div>
                </div>
              </Col>
              <Col md={3} sm={6}>
                <div className="rwm-company-dashboard__stat-card">
                  <div className="rwm-company-dashboard__stat-label">Total Applications</div>
                  <div
                    className="rwm-company-dashboard__stat-value rwm-company-dashboard__stat-value--dark"
                    data-testid="rwm-dashboard-stat-total-applications"
                  >
                    {stats?.total_applications ?? 0}
                  </div>
                </div>
              </Col>
              <Col md={3} sm={6}>
                <div className="rwm-company-dashboard__stat-card">
                  <div className="rwm-company-dashboard__stat-label">Unreviewed Applications</div>
                  <div
                    className="rwm-company-dashboard__stat-value rwm-company-dashboard__stat-value--amber"
                    data-testid="rwm-dashboard-stat-unreviewed"
                  >
                    {stats?.unreviewed_applications ?? 0}
                  </div>
                </div>
              </Col>
              <Col md={3} sm={6}>
                <div className="rwm-company-dashboard__stat-card">
                  <div className="rwm-company-dashboard__stat-label">Credits Remaining</div>
                  <div
                    className="rwm-company-dashboard__stat-value rwm-company-dashboard__stat-value--green"
                    data-testid="rwm-dashboard-stat-credits"
                  >
                    {stats?.credits_remaining ?? 0}
                  </div>
                </div>
              </Col>
            </Row>

            <h2 className="h5 mb-3">Recent Jobs</h2>
            <JobsTable
              jobs={recentJobs}
              onViewApplications={openApplicationsForJob}
              onEdit={canManageJobs ? openEditModal : undefined}
              showArchive={false}
              testIdPrefix="rwm-dashboard-overview"
              canManageJobs={canManageJobs}
            />
          </Tab.Pane>

          <Tab.Pane eventKey="jobs">
            <JobsTable
              jobs={jobs}
              onViewApplications={openApplicationsForJob}
              onEdit={canManageJobs ? openEditModal : undefined}
              showArchive={canManageJobs}
              onArchive={canManageJobs ? handleArchive : undefined}
              onFeature={canManageJobs ? handleFeatureJob : undefined}
              featuringJobId={featuringJobId}
              testIdPrefix="rwm-dashboard-jobs"
              canManageJobs={canManageJobs}
            />
          </Tab.Pane>

          <Tab.Pane eventKey="applications">
            <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
              <Form.Group className="mb-0">
                <Form.Label className="me-2 mb-0">Filter by job</Form.Label>
                <Form.Select
                  value={jobFilter}
                  onChange={(e) => setJobFilter(e.target.value)}
                  style={{ minWidth: '14rem' }}
                  data-testid="rwm-applications-job-filter"
                  aria-label="Filter applications by job"
                >
                  <option value="all">All Jobs</option>
                  {jobs.map((job) => (
                    <option key={job.id} value={String(job.id)}>
                      {job.title}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            {appsLoading ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" />
              </div>
            ) : filteredApplications.length === 0 ? (
              <p className="text-muted">No applications found.</p>
            ) : (
              <div className="table-responsive">
                <Table className="rwm-company-dashboard__table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Candidate Name</th>
                      <th>Applied</th>
                      <th>Current Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredApplications.map((app) => (
                      <tr key={app.id}>
                        <td>{app.candidate_name}</td>
                        <td>{formatPostedDate(app.applied_at)}</td>
                        <td>
                          {canManageApplications ? (
                            <>
                              <Form.Select
                                size="sm"
                                value={app.status}
                                disabled={updatingId === app.id}
                                onChange={(e) => handleStatusChange(app.id, e.target.value)}
                                data-testid={`rwm-dashboard-status-${app.id}`}
                                aria-label={`Status for ${app.candidate_name}`}
                              >
                                {[
                                  {
                                    value: app.status,
                                    label:
                                      APPLICATION_STATUSES.find((s) => s.value === app.status)?.label ||
                                      app.status,
                                    disabled: true,
                                  },
                                  ...(app.valid_next_statuses || []).map((value) => ({
                                    value,
                                    label:
                                      APPLICATION_STATUSES.find((s) => s.value === value)?.label || value,
                                    disabled: false,
                                  })),
                                ].map((opt) => (
                                  <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                                    {opt.label}
                                  </option>
                                ))}
                              </Form.Select>
                              {statusErrors[app.id] ? (
                                <div className="text-danger small mt-1">{statusErrors[app.id]}</div>
                              ) : null}
                            </>
                          ) : (
                            <Badge bg="secondary" data-testid={`rwm-dashboard-status-readonly-${app.id}`}>
                              {APPLICATION_STATUSES.find((s) => s.value === app.status)?.label ||
                                app.status}
                            </Badge>
                          )}
                        </td>
                        <td>
                          <div className="d-flex flex-column flex-sm-row gap-2 align-items-sm-center">
                            {canManageApplications && app.has_cv ? (
                              <Button
                                variant="outline-info"
                                size="sm"
                                disabled={cvDownloadingId === app.id}
                                onClick={() => handleCvDownload(app.id)}
                                data-testid={`rwm-application-cv-download-${app.id}`}
                              >
                                {cvDownloadingId === app.id ? 'Loading…' : 'Download CV'}
                              </Button>
                            ) : null}
                            {canManageApplications && app.candidate_id ? (
                              app.is_blocked ? (
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  disabled={blockUpdatingId === app.id}
                                  onClick={() => handleBlockToggle(app)}
                                  data-testid={`rwm-app-unblock-candidate-${app.id}`}
                                >
                                  {blockUpdatingId === app.id ? 'Updating…' : 'Unblock'}
                                </Button>
                              ) : (
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  disabled={blockUpdatingId === app.id}
                                  onClick={() => handleBlockToggle(app)}
                                  data-testid={`rwm-app-block-candidate-${app.id}`}
                                >
                                  {blockUpdatingId === app.id ? 'Updating…' : 'Block Candidate'}
                                </Button>
                              )
                            ) : null}
                            {jobFilter === 'all' ? (
                              <span className="text-muted small">{app.job_title}</span>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Tab.Pane>

          <Tab.Pane eventKey="messages" unmountOnExit>
            <CompanyMessages token={token} onUnreadChange={setUnreadMessageCount} />
          </Tab.Pane>

          <Tab.Pane eventKey="settings">
            <CompanySettingsTab token={token} />
          </Tab.Pane>

          {canManageTeam ? (
            <Tab.Pane eventKey="team" unmountOnExit>
              <CompanyTeamTab token={token} />
            </Tab.Pane>
          ) : null}
        </Tab.Content>
      </Tab.Container>

      {showEditModal && editJobId ? (
        <EditJobModal
          jobId={editJobId}
          token={token}
          onClose={closeEditModal}
          onSaved={handleEditSaved}
          handleAuthError={handleAuthError}
        />
      ) : null}
    </Container>
  );
}
